import { useCallback, useContext, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { oneDark } from '@codemirror/theme-one-dark';
import { PyodideContext } from './PyodideContext';
import { runScratch } from '../../hooks/usePyodide';

const STORAGE_KEY = 'pylab-scratchpad';

function loadSaved() {
  try { return localStorage.getItem(STORAGE_KEY) || ''; } catch { return ''; }
}

export default function Scratchpad() {
  const { pyodide } = useContext(PyodideContext);
  const [code, setCode] = useState(loadSaved);
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState(null);

  const handleCodeChange = useCallback((val) => {
    setCode(val);
    try { localStorage.setItem(STORAGE_KEY, val); } catch { /* ignore */ }
  }, []);

  const handleRun = useCallback(async () => {
    if (!pyodide || running) return;
    setRunning(true);
    setOutput(null);
    try {
      const result = await runScratch(pyodide, code);
      setOutput(result);
    } catch (err) {
      setOutput({ stdout: '', stderr: '', error: err.message });
    } finally {
      setRunning(false);
    }
  }, [pyodide, code, running]);

  return (
    <div className="pylab-scratchpad">
      <div className="pylab-scratchpad-editor">
        <CodeMirror
          value={code}
          extensions={[python()]}
          theme={oneDark}
          onChange={handleCodeChange}
          basicSetup={{ lineNumbers: true, foldGutter: false, autocompletion: true }}
          placeholder="# Free scratchpad — isolated from the lesson"
        />
      </div>

      <div className="pylab-cell-actions pylab-scratchpad-actions">
        <button
          type="button"
          className="pylab-run-btn"
          onClick={handleRun}
          disabled={!pyodide || running}
        >
          {running
            ? <><span className="pylab-spinner" aria-hidden="true" /> Running…</>
            : <><span aria-hidden="true">▶</span> Run</>}
        </button>
        {output && (
          <button type="button" className="pylab-clear-btn" onClick={() => setOutput(null)}>
            Clear
          </button>
        )}
        <span className="pylab-scratchpad-note">Does not affect lesson variables</span>
      </div>

      {output && (
        <div className="pylab-output pylab-scratchpad-output">
          {output.stdout && <pre className="pylab-stdout">{output.stdout}</pre>}
          {output.stderr && <pre className="pylab-stderr">{output.stderr}</pre>}
          {output.error && <pre className="pylab-error">{output.error}</pre>}
          {!output.stdout && !output.stderr && !output.error && (
            <span className="pylab-output-done">Done — no output</span>
          )}
        </div>
      )}
    </div>
  );
}
